import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuid } from 'uuid'
import { generateApiErrorPayload } from 'api-client/helpers'
import type { VMHAStatusDetails } from 'api-client/model'
import { useAppSelector } from 'app/store'
import Alert from 'core/components/Alert'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import { default as PicklistField } from 'core/components/validatedForm/DropdownField'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import MultiDropdownField from 'core/components/validatedForm/MultiDropdownField'
import Button from 'core/elements/button/Button'
import AsyncDropdown from 'core/elements/dropdown/AsyncDropdown'
import DropdownItemWithTooltip from 'core/elements/dropdown/DropdownItemWithTooltip'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import type Theme from 'core/themes/model'
import {
  addHostToAggregate,
  assignDns,
  assignHostConfig,
  assignHypervisorRole,
  assignImageLibraryRole,
  assignPersistentStorageRole,
  checkVMHAStatusForCluster,
  getBlueprints,
  listResmgrHosts,
  unassignHostConfig,
  updateRemoteSupportRole,
} from '../actions'
import HostNetworkConfigPicklist from '../hosts/HostNetworkConfigPicklist'
import { resmgrHostsSelector, UNASSIGNED_CLUSTER } from '../selectors'
import StretchedClusterPicklist from '../stretched-clusters/components/StretchedClusterPicklist'
import { stretchedClustersByClusterSelector } from '../stretched-clusters/selectors'
import { getHostEligibilityError, mapVMHAConditions } from './helpers'
import type { IClusterHostGroup } from './model'
import VmhaConditionsStatus from './VmhaConditionsStatus'

interface StorageBackends {
  [type: string]: {
    [name: string]: {
      driver?: string
    }
  }
}

interface RoleParams {
  hostNetworkConfig: string | null
  imageLibrary: boolean
  volumeBackends: string[]
  remoteSupport: boolean
  dns: boolean
  stretchedCluster: string | null
}

interface HostEntry {
  id: string
  hostId: string
  roleParams: RoleParams
  error: string | null
}

interface Props {
  selectedCluster: IClusterHostGroup
  onClose: () => void
  reload: () => void
}

const defaultRoleParams = (): RoleParams => ({
  hostNetworkConfig: null,
  imageLibrary: false,
  volumeBackends: [],
  remoteSupport: false,
  dns: false,
  stretchedCluster: null,
})

const createEntry = (): HostEntry => ({
  id: uuid(),
  hostId: '',
  roleParams: defaultRoleParams(),
  error: null,
})

interface HostDropdownCellProps {
  entryId: string
  currentValue: string
  items: { label: string; value: string; disabled?: boolean; content?: React.ReactNode }[]
  hostsLoading: boolean
  onChange: (hostId: string) => void
}

const HostDropdownCell = ({
  entryId,
  currentValue,
  items,
  hostsLoading,
  onChange,
}: HostDropdownCellProps) => (
  <AsyncDropdown
    name={`host-${entryId}`}
    placeholder="Select Host"
    loading={hostsLoading}
    items={items}
    value={currentValue}
    formField
    onChange={onChange}
  />
)

export default function AddHostToClusterModal({ selectedCluster, onClose, reload }: Props) {
  const classes = useStyles()

  const [entries, setEntries] = useState<HostEntry[]>([createEntry()])
  const [eligibilityErrors, setEligibilityErrors] = useState<Map<string, string>>(new Map())
  const [vmhaWarnings, setVmhaWarnings] = useState<Map<string, VMHAStatusDetails>>(new Map())
  const [_loadingVmha, setLoadingVmha] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [storageBackends, setStorageBackends] = useState<StorageBackends>({})

  const stretchedClustersByCluster = useSelector(stretchedClustersByClusterSelector)
  const clusterHasFailureDomains = !!stretchedClustersByCluster[selectedCluster.name]
  const { update: addHostToFD } = useUpdateAction(addHostToAggregate)

  useEffect(() => {
    let isMounted = true

    getBlueprints()
      .then((blueprints) => {
        if (isMounted) {
          setStorageBackends(blueprints?.[0]?.storageBackends ?? {})
        }
      })
      .catch(() => {})

    return () => {
      isMounted = false
    }
  }, [])

  const volumeBackendConfigOptions = useMemo(() => {
    return Object.keys(storageBackends).flatMap((type) =>
      Object.keys(storageBackends[type] ?? {}).map((name) => ({
        label: `${name} (${type}, ${storageBackends[type][name]?.driver ?? ''})`,
        value: name,
      })),
    )
  }, [storageBackends])

  const { loading: hostsLoading } = useListAction(listResmgrHosts)
  const allHosts = useAppSelector(resmgrHostsSelector)

  const unassignedHosts = useMemo(
    () => allHosts.filter((h) => !h.clusterName || h.clusterName === UNASSIGNED_CLUSTER),
    [allHosts],
  )

  const selectedHostIds = useMemo(
    () => new Set(entries.map((e) => e.hostId).filter(Boolean)),
    [entries],
  )

  // Hosts not yet selected in any row — used to decide whether to show "+ Add Host"
  const availableHosts = useMemo(
    () => unassignedHosts.filter((h) => !selectedHostIds.has(h.id)),
    [unassignedHosts, selectedHostIds],
  )

  const hasEligibilityError = Array.from(eligibilityErrors.values()).some(Boolean)
  const hasNoSelections = entries.every((e) => !e.hostId)
  const hasConfigError = entries.some(
    (e) =>
      e.hostId && (!e.roleParams.hostNetworkConfig || e.roleParams.hostNetworkConfig === 'none'),
  )
  const hasStretchedClusterError =
    clusterHasFailureDomains && entries.some((e) => e.hostId && !e.roleParams.stretchedCluster)
  const baseCols = clusterHasFailureDomains ? 8 : 7
  const colSpan = entries.length > 1 ? baseCols + 1 : baseCols
  const selectedCount = entries.filter((e) => e.hostId).length
  const submitTitle = selectedCount > 1 ? 'Add Hosts' : 'Add Host'

  const addEntry = useCallback(() => {
    setEntries((prev) => [...prev, createEntry()])
  }, [])

  const removeEntry = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id))
    setEligibilityErrors((prev) => {
      const next = new Map(prev)
      next.delete(id)
      return next
    })
    setVmhaWarnings((prev) => {
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  const handleHostChange = useCallback(
    async (entry: HostEntry, hostId: string) => {
      const host = allHosts.find((h) => h.id === hostId)
      const hostNetworkConfig = host?.hostconfig_id || null

      setEntries((prev) =>
        prev.map((e) =>
          e.id === entry.id
            ? {
                ...e,
                hostId,
                error: null,
                roleParams: { ...e.roleParams, hostNetworkConfig },
              }
            : e,
        ),
      )
      setEligibilityErrors((prev) => {
        const next = new Map(prev)
        next.delete(entry.id)
        return next
      })
      setVmhaWarnings((prev) => {
        const next = new Map(prev)
        next.delete(entry.id)
        return next
      })

      if (!hostId) return
      if (!host) return

      const errMsg = getHostEligibilityError(host, selectedCluster)
      if (errMsg) {
        setEligibilityErrors((prev) => new Map(prev).set(entry.id, errMsg))
        return
      }

      if (selectedCluster.vmHighAvailability?.enabled) {
        setLoadingVmha((prev) => new Set(prev).add(entry.id))
        try {
          const status = await checkVMHAStatusForCluster(selectedCluster.name)
          const hasFailedCondition =
            status?.response?.conditions &&
            Object.values(status.response.conditions).some((v) => v === 'failed')
          if (hasFailedCondition) {
            setVmhaWarnings((prev) => new Map(prev).set(entry.id, status?.response))
          }
        } catch {
          // fail silently
        } finally {
          setLoadingVmha((prev) => {
            const next = new Set(prev)
            next.delete(entry.id)
            return next
          })
        }
      }
    },
    [allHosts, selectedCluster],
  )

  const updateEntryRoles = useCallback((id: string, patch: Partial<RoleParams>) => {
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, roleParams: { ...e.roleParams, ...patch } } : e)),
    )
  }, [])

  const handleSubmit = useCallback(async () => {
    setSubmitting(true)

    const results = await Promise.all(
      entries
        .filter((e) => e.hostId)
        .map(async (entry) => {
          try {
            const host = allHosts.find((h) => h.id === entry.hostId)
            if (!host) return { id: entry.id, error: 'Host not found.' }

            const roles = entry.roleParams

            if (host.hostconfig_id && host.hostconfig_id !== roles.hostNetworkConfig) {
              await unassignHostConfig({ hostId: host.id, configId: host.hostconfig_id })
            }
            if (
              roles.hostNetworkConfig &&
              roles.hostNetworkConfig !== 'none' &&
              host.hostconfig_id !== roles.hostNetworkConfig
            ) {
              await assignHostConfig({ hostId: host.id, configId: roles.hostNetworkConfig })
            }

            await assignHypervisorRole({ host, hostcluster: selectedCluster.name })
            if (roles.imageLibrary) {
              await assignImageLibraryRole({ host })
            }
            if (roles.volumeBackends?.length > 0) {
              await assignPersistentStorageRole({ host, body: { backends: roles.volumeBackends } })
            }
            if (roles.remoteSupport) {
              await updateRemoteSupportRole({ host })
            }
            if (roles.dns) {
              await assignDns({ host })
            }
            if (roles.stretchedCluster) {
              const fdResult = await addHostToFD({
                id: roles.stretchedCluster,
                body: { add_host: { host: host.id } },
              })
              if (!fdResult.success) {
                const errorPayload = generateApiErrorPayload(
                  fdResult.error,
                  '',
                  'Failed to assign host to failure domain.',
                )
                return { id: entry.id, error: errorPayload?.message }
              }
            }
            return { id: entry.id, error: null }
          } catch (err) {
            const errorMessage = generateApiErrorPayload(
              err,
              '',
              'An error occurred while assigning roles to the host.',
            )
            return { id: entry.id, error: errorMessage?.message }
          }
        }),
    )

    setSubmitting(false)

    const hasErrors = results.some((r) => r.error)
    if (hasErrors) {
      setEntries((prev) =>
        prev.map((e) => {
          const result = results.find((r) => r.id === e.id)
          return result ? { ...e, error: result.error } : e
        }),
      )
    } else {
      reload()
      onClose()
    }
  }, [entries, allHosts, selectedCluster, reload, onClose, addHostToFD])

  return (
    <ModalForm
      open
      title="Add Host to Cluster"
      onSubmit={handleSubmit}
      onClose={onClose}
      submitting={submitting}
      submitTitle={submitTitle}
      maxWidth={1360}
      panel="dialog"
      disableSubmit={
        hasEligibilityError || hasNoSelections || hasConfigError || hasStretchedClusterError
      }
      showConfirmationOnCancel={!hasNoSelections}
    >
      <Text variant="body2" className={classes.description}>
        Assign hosts to the cluster. The hypervisor role is enabled automatically.
      </Text>
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            <tr className={classes.headerRow}>
              <th className={`${classes.headerCell} ${classes.colHost}`}>
                <Text variant="caption1">Host</Text>
              </th>
              <th className={`${classes.headerCell} ${classes.colHostConfig}`}>
                <Text variant="caption1">Host Config</Text>
              </th>
              <th className={`${classes.headerCell} ${classes.colHypervisorCluster}`}>
                <TitleWithInfoIcon
                  titleText={<Text variant="caption1">Hypervisor Cluster</Text>}
                  tooltipMsg="Hypervisor role is required when adding a host to a cluster"
                />
              </th>
              {clusterHasFailureDomains && (
                <th className={`${classes.headerCell} ${classes.colStretchedCluster}`}>
                  <TitleWithInfoIcon
                    tooltipMsg="Failure Domains define the sites within a cluster and determine VM placement and recovery behavior. Every host in a cluster configured with failure domains must be assigned to one"
                    titleText={<Text variant="caption1">Failure Domain</Text>}
                  />
                </th>
              )}
              <th className={`${classes.headerCell} ${classes.colVmImage}`}>
                <Text variant="caption1">Image Library</Text>
              </th>
              <th className={`${classes.headerCell} ${classes.colPersistentStorage}`}>
                <Text variant="caption1">Persistent Storage</Text>
              </th>
              <th className={`${classes.headerCell} ${classes.colRemoteSupport}`}>
                <Text variant="caption1">Advanced Remote Support</Text>
              </th>
              <th className={`${classes.headerCell} ${classes.colDns}`}>
                <Text variant="caption1">DNS</Text>
              </th>
              {entries.length > 1 && (
                <th className={`${classes.headerCell} ${classes.colRemove}`} />
              )}
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              const items = unassignedHosts.map((host) => {
                const selectedInOtherRow = selectedHostIds.has(host.id) && entry.hostId !== host.id
                const eligibilityMsg = selectedInOtherRow
                  ? null
                  : getHostEligibilityError(host, selectedCluster)
                const isDisabled = selectedInOtherRow || !!eligibilityMsg
                const tooltipMsg = selectedInOtherRow
                  ? 'This host is already selected in another row.'
                  : eligibilityMsg || ''

                return isDisabled
                  ? {
                      label: host.name,
                      content: (
                        <DropdownItemWithTooltip
                          label={host.name}
                          tooltip={tooltipMsg}
                          icon="circle-info"
                          disabled
                        />
                      ),
                      value: host.id,
                      disabled: true,
                    }
                  : { label: host.name, value: host.id }
              })

              return (
                <React.Fragment key={entry.id}>
                  <tr className={classes.bodyRow}>
                    <td className={`${classes.bodyCell} ${classes.colHost}`}>
                      <HostDropdownCell
                        entryId={entry.id}
                        currentValue={entry.hostId}
                        items={items}
                        hostsLoading={hostsLoading}
                        onChange={(hostId: string) => handleHostChange(entry, hostId)}
                      />
                    </td>
                    <td className={`${classes.bodyCell} ${classes.colHostConfig}`}>
                      <div className={classes.fieldWrapper}>
                        <PicklistField
                          DropdownComponent={HostNetworkConfigPicklist}
                          name="hostNetworkConfig"
                          id={`hostNetworkConfig-${entry.id}`}
                          label=""
                          compact={false}
                          value={entry.roleParams.hostNetworkConfig || ''}
                          disabled={!entry.hostId}
                          onChange={(value: string) =>
                            updateEntryRoles(entry.id, { hostNetworkConfig: value })
                          }
                        />
                        {entry.hostId &&
                          (!entry.roleParams.hostNetworkConfig ||
                            entry.roleParams.hostNetworkConfig === 'none') && (
                            <ErrorMessage className={classes.errorText}>
                              Select a valid host config
                            </ErrorMessage>
                          )}
                      </div>
                    </td>
                    <td className={`${classes.bodyCell} ${classes.colHypervisorCluster}`}>
                      <div className={classes.hypervisorClusterContainer}>
                        <div className={classes.checkboxWrapper}>
                          <CheckboxField
                            id={`hypervisor-${entry.id}`}
                            label=""
                            value={true}
                            readOnly
                            info="Hypervisor role is required when adding a host to a cluster"
                          />
                        </div>
                        <Text variant="body2">{selectedCluster.name}</Text>
                      </div>
                    </td>
                    {clusterHasFailureDomains && (
                      <td className={`${classes.bodyCell} ${classes.colStretchedCluster}`}>
                        <PicklistField
                          DropdownComponent={StretchedClusterPicklist}
                          id={`stretchedCluster-${entry.id}`}
                          label=""
                          clusterName={selectedCluster.name}
                          value={entry.roleParams.stretchedCluster || ''}
                          disabled={!entry.hostId}
                          compact={false}
                          onChange={(value: string) =>
                            updateEntryRoles(entry.id, { stretchedCluster: value })
                          }
                          required
                        />
                      </td>
                    )}
                    <td className={`${classes.bodyCell} ${classes.colVmImage}`}>
                      <CheckboxField
                        id={`imageLibrary-${entry.id}`}
                        label=""
                        className={classes.marginAuto}
                        value={entry.roleParams.imageLibrary}
                        disabled={!entry.hostId}
                        onChange={(value: boolean) =>
                          updateEntryRoles(entry.id, { imageLibrary: value })
                        }
                      />
                    </td>
                    <td className={`${classes.bodyCell} ${classes.colPersistentStorage}`}>
                      <MultiDropdownField
                        id={`volumeBackends-${entry.id}`}
                        label=""
                        options={volumeBackendConfigOptions}
                        value={entry.roleParams.volumeBackends}
                        disabled={!entry.hostId}
                        onChange={(value: string[]) =>
                          updateEntryRoles(entry.id, { volumeBackends: value })
                        }
                        fullWidth
                      />
                    </td>
                    <td className={`${classes.bodyCell} ${classes.colRemoteSupport}`}>
                      <CheckboxField
                        id={`remoteSupport-${entry.id}`}
                        label=""
                        className={classes.marginAuto}
                        value={entry.roleParams.remoteSupport}
                        disabled={!entry.hostId}
                        onChange={(value: boolean) =>
                          updateEntryRoles(entry.id, { remoteSupport: value })
                        }
                      />
                    </td>
                    <td className={`${classes.bodyCell} ${classes.colDns}`}>
                      <CheckboxField
                        id={`dns-${entry.id}`}
                        label=""
                        className={classes.marginAuto}
                        value={entry.roleParams.dns}
                        disabled={!entry.hostId}
                        onChange={(value: boolean) => updateEntryRoles(entry.id, { dns: value })}
                      />
                    </td>
                    {entries.length > 1 && (
                      <td className={`${classes.bodyCell} ${classes.colRemove}`}>
                        <button
                          type="button"
                          className={classes.removeButton}
                          onClick={() => removeEntry(entry.id)}
                          aria-label="Remove row"
                        >
                          ✕
                        </button>
                      </td>
                    )}
                  </tr>
                  {eligibilityErrors.get(entry.id) && (
                    <tr>
                      <td colSpan={colSpan} className={classes.alertCell}>
                        <Alert variant="error" message={eligibilityErrors.get(entry.id)} />
                      </td>
                    </tr>
                  )}
                  {vmhaWarnings.get(entry.id) && (
                    <tr>
                      <td colSpan={colSpan} className={classes.alertCell}>
                        <Alert variant="warning" title="VMHA Warning">
                          <VmhaConditionsStatus
                            title="The following VMHA conditions may be impacted:"
                            conditions={mapVMHAConditions(vmhaWarnings.get(entry.id))}
                            plainText
                          />
                        </Alert>
                      </td>
                    </tr>
                  )}
                  {entry.error && (
                    <tr>
                      <td colSpan={colSpan} className={classes.alertCell}>
                        <Alert variant="error" message={entry.error} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
            {availableHosts.length > 0 && (
              <tr>
                <td colSpan={colSpan} className={classes.addHostCell}>
                  <Button type="button" variant="secondary" onClick={addEntry}>
                    + Add Another Host
                  </Button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  description: {
    color: theme.components.typography.passive,
    marginBottom: 24,
    lineHeight: 1.5,
  },
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  tableContainer: {
    border: `1px solid ${theme.components.table.border}`,
    borderRadius: 4,
    overflowX: 'auto',
    overflowY: 'hidden',
    marginBottom: 8,
  },
  table: {
    width: '100%',
    minWidth: 1100,
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  headerRow: {
    borderBottom: `1px solid ${theme.components.table.border}`,
    backgroundColor: theme.components.table.hoverBackground,
  },
  headerCell: {
    padding: '16px 12px',
    textAlign: 'left',
    fontWeight: 400,
    verticalAlign: 'top',
    color: theme.components.typography.passive,
    fontSize: 12,
  },
  colHostConfig: {
    width: '14%',
  },
  colHypervisorCluster: {
    width: '18%',
  },
  colHost: {
    width: '20%',
  },
  colStretchedCluster: {
    width: '14%',
  },
  colVmImage: {
    width: '8%',
    textAlign: 'center',
  },
  colPersistentStorage: {
    width: '18%',
  },
  colRemoteSupport: {
    width: '11%',
    textAlign: 'center',
  },
  colDns: {
    width: '5%',
    textAlign: 'center',
  },
  colRemove: {
    width: '4%',
    textAlign: 'center',
  },
  hypervisorClusterContainer: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: '4px',
    gap: 8,
    ...theme.typography.inputPlaceholder,
    marginTop: 1,
    backgroundColor: theme.components.input.frame.background,
    color: theme.components.input.frame.color,
    borderColor: theme.components.input.frame.border,
    borderWidth: 1,
    borderStyle: 'solid',
    padding: theme.spacing(0, 1),
    minHeight: 36,
  },
  checkboxWrapper: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 4,
  },
  bodyRow: {
    borderBottom: `1px solid ${theme.components.table.border}`,
  },
  bodyCell: {
    padding: '16px 12px',
    verticalAlign: 'top',
  },
  marginAuto: {
    margin: 'auto',
    paddingTop: 8,
  },
  alertCell: {
    padding: '8px 12px',
    borderBottom: `1px solid ${theme.components.table.border}`,
  },
  errorText: {
    fontSize: 12,
    gridTemplateColumns: '20px auto',
  },
  addHostCell: {
    padding: '12px 16px',
  },
  removeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: theme.components.typography.passive,
    fontSize: 14,
    padding: '4px 8px',
    '&:hover': {
      color: theme.components.alert.error.background,
    },
  },
}))
